import React, { useEffect, useState } from "react";
import { makeStyles, createStyles, Theme, useTheme } from "@material-ui/core/styles";
import Spacer from "components/Spacer";
import StyledButton from "components/buttons/StyledButton";
import INewsArticle, { IWidget } from "apiConsumers/newsApiConsumer/models/INewsArticle";
import { newsApi } from "apiConsumers";
import RouterLink from "components/links/RouterLink";
import { useLocation } from "react-router-dom";
import ArticleContent from "../ArticleContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import calculateAspectRatio from "utils/calculateAspectRatio";
import imageSizes from "imageSizes";
import TextBoxWithImage from "components/textBoxes/textboxWithImage/TextBoxWithImage";
import { transparentColour, fonts, newsItemHoverOpacity } from "theme";
import { useMediaQuery } from "@material-ui/core";

const useStyles = makeStyles((theme: Theme) => {
    return createStyles({
        root: {},
        linkContainer: {
            "&:hover": {
                opacity: newsItemHoverOpacity,
            },
        },
        textContainer: {
            [theme.breakpoints.up("lg")]: {
                maxWidth: 450,
            },
            [theme.breakpoints.down("md")]: {
                minWidth: 200,
            },
            color: theme.palette.common.white,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
        },
        articleContent: {
            overflow: "hidden",
            position: "relative",
            fontFamily: fonts.secondary,
            height: 90,
            padding: theme.spacing(1),
        },
        blurField: {
            width: "100%",
            bottom: 0,
            position: "absolute",
            maxWidth: "inherit",
            height: "100%",
            backgroundImage: `linear-gradient(${transparentColour}, #000000b0)`,
            pointerEvents: "none",
        },
        descriptionContainerContent: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
        },
    });
});

interface IProps {
    article?: INewsArticle;
    isLoading?: boolean;
}

export default function ArticleOverviewWithDescription(props: IProps) {
    const { article = {}, isLoading } = props;
    const location = useLocation();

    const theme = useTheme();
    const smScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const { featureImage = {}, title, url } = article;
    const { url: featureImageSrc = "" } = featureImage;
    const classes = useStyles();

    const [articleContent, setArticleContent] = useState<Array<IWidget>>();
    const [isArticleContentLoading, setIsArticleContentLoading] = useState<boolean>(true);

    useEffect(() => {
        setIsArticleContentLoading(true);
        if (typeof url === "undefined") {
            setArticleContent(undefined);
            setIsArticleContentLoading(false);
        } else {
            newsApi.getArticle(url).then((_article) => {
                const { content } = _article;

                if (typeof content !== "undefined") setArticleContent(content);

                setIsArticleContentLoading(false);
            });
        }
    }, [url]);

    const content = (
        <TextBoxWithImage
            heading={title}
            imageProps={{
                aspectRatio: calculateAspectRatio(imageSizes.newsArticle),
                src: featureImageSrc,
                isLoading: isLoading,
            }}
            classes={{ descriptionContainerContent: classes.descriptionContainerContent }}
            mobile={smScreen}
        >
            <div className={classes.textContainer}>
                {isLoading ? null : (
                    <>
                        <div className={classes.articleContent}>
                            {isArticleContentLoading ? (
                                <div style={{ display: "flex", justifyContent: "center" }}>
                                    <CircularProgress color="inherit" />
                                </div>
                            ) : typeof articleContent === "undefined" ? null : (
                                <ArticleContent articleContent={articleContent} />
                            )}
                            <div className={classes.blurField}></div>
                        </div>

                        <Spacer size={2} />

                        <div>
                            <StyledButton>Learn More</StyledButton>
                        </div>
                    </>
                )}
            </div>
        </TextBoxWithImage>
    );

    if (typeof url === "undefined") return <div>{content}</div>;

    const articleLink = `${location.pathname}/${url}`;

    return (
        <RouterLink
            to={articleLink}
            className={classes.linkContainer}
            style={{ textDecoration: "none" }}
        >
            {content}
        </RouterLink>
    );
}
